import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiPackage,
  FiClock,
  FiTruck,
  FiCheckCircle,
  FiMapPin,
  FiCalendar,
  FiEye,
} from "react-icons/fi";

const Card = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #e9ecef;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  background: ${(props) => {
    switch (props.status) {
      case "delivered":
        return "linear-gradient(135deg, #28a745, #20c997)";
      case "out_for_delivery":
        return "linear-gradient(135deg, #ffc107, #fd7e14)";
      case "preparing":
        return "linear-gradient(135deg, #007bff, #6610f2)";
      case "confirmed":
        return "linear-gradient(135deg, #6c757d, #495057)";
      default:
        return "linear-gradient(135deg, #dc3545, #e83e8c)";
    }
  }};
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
`;

const StatusIcon = styled.div`
  font-size: 1.5rem;
  margin-right: 0.75rem;
`;

const StatusText = styled.div`
  font-weight: 600;
  text-transform: capitalize;
`;

const TrackingNumber = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  font-family: monospace;
  letter-spacing: 1px;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.9rem;
`;

const InfoIcon = styled.div`
  margin-right: 0.5rem;
  color: #007bff;
`;

const MealInfo = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const MealName = styled.div`
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
`;

const VendorName = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
`;

const DeliveryInfo = styled.div`
  background: #e3f2fd;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
`;

const DeliveryAddress = styled.div`
  color: #495057;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const DeliveryDate = styled.div`
  color: #6c757d;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
`;

const CardFooter = styled.div`
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OrderId = styled.div`
  color: #6c757d;
  font-size: 0.85rem;
  font-family: monospace;
`;

const ViewButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #0056b3;
  }
`;

const getStatusIcon = (status) => {
  switch (status) {
    case "delivered":
      return <FiCheckCircle />;
    case "out_for_delivery":
      return <FiTruck />;
    case "preparing":
      return <FiPackage />;
    case "confirmed":
      return <FiCheckCircle />;
    default:
      return <FiClock />;
  }
};

const getStatusMessage = (status) => {
  switch (status) {
    case "delivered":
      return "Delivered";
    case "out_for_delivery":
      return "Out for Delivery";
    case "preparing":
      return "Preparing";
    case "confirmed":
      return "Confirmed";
    case "pending":
      return "Pending";
    case "cancelled":
      return "Cancelled";
    default:
      return "Unknown";
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (timeString) => {
  if (!timeString) return "";
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const OrderCard = ({ order }) => {
  const navigate = useNavigate();

  const handleViewOrder = () => {
    navigate(`/orders/${order.id}`);
  };

  return (
    <Card
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleViewOrder}
    >
      <CardHeader status={order.status}>
        <StatusInfo>
          <StatusIcon>{getStatusIcon(order.status)}</StatusIcon>
          <StatusText>{getStatusMessage(order.status)}</StatusText>
        </StatusInfo>
        {order.tracking_number && (
          <TrackingNumber>{order.tracking_number}</TrackingNumber>
        )}
      </CardHeader>

      <CardContent>
        <OrderInfo>
          <InfoItem>
            <InfoIcon>
              <FiCalendar />
            </InfoIcon>
            {formatDate(order.delivery_date)}
          </InfoItem>
          {order.estimated_delivery_time && (
            <InfoItem>
              <InfoIcon>
                <FiClock />
              </InfoIcon>
              {formatTime(order.estimated_delivery_time)}
            </InfoItem>
          )}
        </OrderInfo>

        <MealInfo>
          <MealName>
            {order.meal_name || order.standalone_meal_name || order.meal_plan_title || "Meal Order"}
          </MealName>
          <VendorName>{order.business_name}</VendorName>
          {order.meal_plan_title && order.meal_name && (
            <div style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: "0.25rem" }}>
              From: {order.meal_plan_title}
            </div>
          )}
        </MealInfo>

        <DeliveryInfo>
          <DeliveryAddress>
            <FiMapPin style={{ marginRight: "0.5rem" }} />
            {order.delivery_address}
          </DeliveryAddress>
          {order.delivery_instructions && (
            <DeliveryDate>
              <strong>Instructions:</strong> {order.delivery_instructions}
            </DeliveryDate>
          )}
        </DeliveryInfo>
      </CardContent>

      <CardFooter>
        <OrderId>Order #{order.id}</OrderId>
        <ViewButton
          onClick={(e) => {
            e.stopPropagation();
            handleViewOrder();
          }}
        >
          <FiEye style={{ marginRight: "0.5rem" }} />
          View Details
        </ViewButton>
      </CardFooter>
    </Card>
  );
};

export default OrderCard;
